import {
  Avatar,
  Button,
  Dropdown,
  Image,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import TableUi from "../../../../atoms/Table";
import Filters from "./Components/Filters";
import { useEffect, useRef, useState } from "react";
import { PopUp } from "../../../../atoms/PopUp";
import { updateMaterialById, deleteMaterial } from "../../ducks/services";
import { AxiosError } from "axios";
import ErrorMessage from "../../../../atoms/Toasts/ErrorMessage";
import EmptyCard from "../../../../atoms/EmptyCard";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CrossIcon,
  DeleteIcon,
  PencilIcon,
  PlusIcon,
  TickIcon,
} from "../../../../atoms/CustomIcons";
import { CaretDownOutlined, MoreOutlined } from "@ant-design/icons";
import SuccessMessage from "../../../../atoms/Toasts/SuccessMessage";
import { formatRelativeTime } from "../../../../atoms/TimeFormatDisplay";
import { MaterialListType } from "../../ducks/types";
import { getMaterialListing } from "../../ducks/actions";
import { useAppDispatch, useAppSelector } from "../../../../config/hooks";
import ConfirmDelete from "../../../../atoms/ConfirmDelete";
import ConfirmStatus from "./Components/PopUps/ConfirmStatus";
import { getCompanyFieldsById } from "../../../CompanySettings/TagsCategories/ducks/actions";

export default function Materials(props: {
  setTabValue: (val: string) => void;
}) {
  const { setTabValue } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const url = useLocation();
  const companyId = url?.pathname.split("/")[3];
  const materialListData = useAppSelector(
    (state) => state?.inventory?.materialListData
  );
  const tagItems = useAppSelector((state) => state?.tags?.tagData);
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableRecord, setTableRecord] = useState<MaterialListType | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAllDeleteModalOpen, setIsAllDeleteModalOpen] = useState(false);
  const [isArchivedModalOpen, setIsArchivedModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [archived, isArchived] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState<{
    category: undefined | string[];
    status: undefined | string;
    search_text?: string;
  }>({
    category: undefined,
    status: undefined,
    search_text: undefined,
  });

  const changeStatus = (val: string) => {
    if (val === "archived") {
      setIsArchivedModalOpen(true);
      return;
    }
    setLoading(true);
    functionChangeStatus(val);
  };

  const functionChangeStatus = (val: string) => {
    const payload = {
      status: val,
    };
    updateMaterialById(tableRecord?.id || "", payload)
      .then(() => {
        setLoading(false);
        setIsArchivedModalOpen(false);
        SuccessMessage(
          val === "archived" ? "Material archived" : "Material is in use now"
        );
        fetchMaterialListing();
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-materials/${companyId}/${tableRecord?.id}`);
          }}
        >
          Edit
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            setIsArchivedModalOpen(false);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusItems = [
    {
      key: "header",
      label: (
        <div
          style={{
            padding: "6px 12px",
            color: "#474C4D",
            fontWeight: 500,
            fontSize: "12px",
            cursor: "default",
          }}
        >
          Change status to:
        </div>
      ),
      disabled: true,
    },
    {
      key: "in_use",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("in_use");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />
          In use
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },

    {
      key: "archived",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("archived");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#98A3A9", borderRadius: "2px" }}
          />{" "}
          Archived{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const columns: TableColumnsType<MaterialListType> = [
    {
      title: "Material name",
      dataIndex: "name",
      width: 600,
      fixed: "left",
      render: (text) =>
        text.length > 40 ? (
          <Tooltip title={text}>{text.substring(0, 60) + "..."}</Tooltip>
        ) : (
          text
        ),
    },
    {
      title: "Categories",
      dataIndex: "category_ids",
      width: 240,
      render: (text) =>
        text?.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {text?.slice(0, 2).map((item: string) => {
              const tagDetails: { color: string; name: string } =
                tagItems?.data?.find(
                  (tag: { id: string }) => tag?.id === item
                ) || { color: "", name: "" };

              return (
                tagDetails?.name && (
                  <Tag
                    key={item}
                    style={{ textTransform: "capitalize" }}
                    className={tagDetails?.color}
                  >
                    {tagDetails?.name}
                  </Tag>
                )
              );
            })}

            {text?.length > 2 && (
              <Tooltip
                title={text
                  ?.slice(2)
                  ?.map((itemId: string) => {
                    const tagDetails: { name: string } = tagItems?.data?.find(
                      (tag: { id: string }) => tag?.id === itemId
                    ) || { name: "" };
                    return tagDetails?.name;
                  })
                  ?.join(", ")}
              >
                <Tag
                  style={{
                    background: "#EFF2F3",
                    color: "#151718",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  +{text?.length - 2}
                </Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: <div style={{ width: "94px", textAlign: "right" }}>Unit cost</div>,
      dataIndex: "cost",
      width: 178,
      render: (text, record) =>
        text ? (
          <div className="unit_wrap">
            <div className="unit_left_text">${text}</div>
            {record?.unit?.singular_name && (
              <div className="unit_right_text">
                / {record?.unit?.singular_name}
              </div>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Cost updated",
      dataIndex: "cost_updated_date",
      width: 140,
      render: (text) => (text ? formatRelativeTime(text) : "-"),
      align: "right",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 129,
      render: (text, record) => (
        <Dropdown
          menu={{
            items: statusItems,
            selectable: true,
            selectedKeys: [text],
          }}
          trigger={["click"]}
          overlayClassName="status_dropdown_overlay"
        >
          <Button
            onClick={(e) => {
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className={`status_dropdown ${
              text === "in_use" ? "active_status" : "archived_status"
            }`}
            ref={dropdownRef}
          >
            {text
              ?.replace("_", " ")
              .replace(/^\w/, (c: string): string => c.toUpperCase())}
            <div className="icon_arrow">
              <CaretDownOutlined />
            </div>
          </Button>
        </Dropdown>
      ),
    },

    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  const deleteMaterialsPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete
            <b>&nbsp;{tableRecord?.name}</b> ? <br /> This action can't be
            undone.
          </>
        }
        btnText="Delete material"
        btnApi={() => apiSubmitDeleteCompany()}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const deleteAllMaterialsPopUp = {
    visibility: isAllDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsAllDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete selected materials? <br />
            This action can't be undone.
          </>
        }
        btnText={`Delete material${selectedRowKeys?.length > 1 ? "s" : ""}`}
        btnApi={() => apiSubmitDeleteCompany()}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsAllDeleteModalOpen(false),
  };

  const confirmArchivedPopUp = {
    visibility: isArchivedModalOpen,
    content: (
      <ConfirmStatus
        onClose={() => setIsArchivedModalOpen(false)}
        apiSubmit={() => functionChangeStatus("archived")}
        loading={loading}
        btnText="Archive material"
        btnClass="mint-btn"
        title="Archive material"
        description={
          <>
            Are you sure you want to archive
            <b>&nbsp;{tableRecord?.name}</b> ? <br /> <br /> This material won’t
            be available in stock, services, estimates, and purchases anymore.
          </>
        }
      />
    ),
    width: 480,
    onCancel: () => setIsArchivedModalOpen(false),
  };

  const apiSubmitDeleteCompany = () => {
    setLoading(true);
    const idsToDelete =
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""];
    deleteMaterial(idsToDelete)
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setIsAllDeleteModalOpen(false);
        setSelectedRowKeys([]);
        fetchMaterialListing();
        const message =
          idsToDelete.length > 1 ? "Materials deleted" : "Material deleted";
        SuccessMessage(message);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const fetchMaterialListing = () => {
    const payload = {
      offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
      limit: limit,

      status:
        filters?.status && filters?.status?.length > 0
          ? filters?.status
          : "in_use",
      company_id: companyId,
      search_text: undefined,
    };
    dispatch(getMaterialListing(payload));
  };

  useEffect(() => {
    if (companyId) {
      const payload = {
        offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
        limit: limit,
        category_ids:
          filters?.category && filters?.category?.length > 0
            ? filters?.category
            : undefined,
        status:
          filters?.status && filters?.status?.length > 0
            ? filters?.status
            : "in_use",
        company_id: companyId,
        search_text: filters?.search_text || undefined,
      };
      dispatch(getMaterialListing(payload));
    }
  }, [dispatch, filters, pagination, limit, companyId]);

  const tableTopItems = [
    // {
    //   key: "1",
    //   label: (
    //     <Button
    //       type="text"
    //       className="no-hover"
    //       onClick={() => navigate(`/add-materials/${companyId}`)}
    //     >
    //       <PlusIcon /> Add materials
    //     </Button>
    //   ),
    // },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={() =>
            navigate(`/company-settings/tags-categories/${companyId}`)
          }
        >
          <PencilIcon /> Edit categories
        </Button>
      ),
    },
    // {
    //   key: "3",
    //   label: (
    //     <Button
    //       type="text"
    //       className="no-hover"
    //       onClick={(e) => {
    //         e.stopPropagation();
    //       }}
    //     >
    //       <DownloadIcon /> Export materials
    //     </Button>
    //   ),
    // },
  ];

  // const buttonItems = [
  //   {
  //     key: "1",
  //     label: (
  //       <Button
  //         type="text"
  //         className="no-hover"
  //         onClick={(e) => {
  //           e.stopPropagation();
  //         }}
  //       >
  //         <PlusIcon /> Update here
  //       </Button>
  //     ),
  //   },
  //   {
  //     key: "2",
  //     label: (
  //       <Button
  //         type="text"
  //         className="no-hover"
  //         onClick={(e) => {
  //           e.stopPropagation();
  //         }}
  //       >
  //         <UploadIcon /> Upload CSV
  //       </Button>
  //     ),
  //   },
  // ];

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "MATERIALS",
      };

      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  return (
    <>
      <TableUi<MaterialListType>
        columns={columns}
        data={{
          count: materialListData?.count,
          data: materialListData?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
            setTabValue={setTabValue}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonIcon={<PlusIcon />}
        // createButtonItem={buttonItems}
        createButtonText="Add materials"
        createButtonLink={`/add-materials/${companyId}`}
        items={tableTopItems}
      />

      {materialListData?.count === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              archived
                ? "There are no archived materials"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} materials`
            }
            description={
              archived
                ? "We believe in our absolute success and hope to never have an archived materials here."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              archived
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "material" : "materials"}`}{" "}
            selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsAllDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...deleteMaterialsPopUp} />
      <PopUp {...deleteAllMaterialsPopUp} />
      <PopUp {...confirmArchivedPopUp} />
    </>
  );
}
